import { Show, SignIn, SignUp, useAuth } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, apiFetch } from '@/api/client';
import { PortalBilling } from '@/components/PortalBilling';
import { PortalOnboarding } from '@/components/PortalOnboarding';
import { PortalSummaryCard, type PortalSummary } from '@/components/PortalSummaryCard';
import { Spinner } from '@/components/ui/spinner';
import { AWAITING_PAYMENT_BANNER, billingUi } from '@/lib/portalBilling';
import { portalTabs, type PortalTab } from '@/lib/portalOnboarding';
import { portalPath } from '@/lib/portalPath';

/** Signed-in portal shell. Before a run is published the customer sees
 *  the onboarding uploads only; once published, the report becomes the
 *  landing tab and onboarding stays accessible via a second tab. */
function SignedInPortal() {
  const { getToken, userId } = useAuth();
  const summary = useQuery({
    queryKey: ['portal', 'summary', userId, null],
    enabled: !!userId,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not signed in');
      return apiFetch<PortalSummary>(portalPath('/api/portal/summary'), { idToken: token });
    },
  });

  if (summary.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  // On error, defer to PortalSummaryCard — its error view carries the
  // "account is being set up / refresh" guidance for the not-yet-
  // provisioned case.
  if (summary.isError || !summary.data) {
    return <PortalSummaryCard />;
  }

  const { showSummary, defaultTab } = portalTabs(summary.data.has_published_engagement);
  if (!showSummary) {
    return <PortalOnboarding />;
  }
  return <PublishedPortal defaultTab={defaultTab} summary={summary.data} />;
}

function PublishedPortal({ defaultTab, summary }: { defaultTab: PortalTab; summary: PortalSummary }) {
  const [tab, setTab] = useState<PortalTab>(defaultTab);
  const billing = billingUi(summary);
  return (
    <div className="grid gap-6">
      {billing.awaitingPayment && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-900">
          {AWAITING_PAYMENT_BANNER}
        </div>
      )}
      <div className="flex gap-1 border-b border-border">
        <TabButton active={tab === 'summary'} onClick={() => setTab('summary')}>
          Report
        </TabButton>
        <TabButton active={tab === 'onboarding'} onClick={() => setTab('onboarding')}>
          Documents
        </TabButton>
        {billing.showBillingTab && (
          <TabButton active={tab === 'billing'} onClick={() => setTab('billing')}>
            <span className="flex items-center gap-1.5">
              Billing
              {billing.awaitingPayment && (
                <span
                  aria-label="payment required"
                  className="inline-block h-2 w-2 rounded-full bg-red-500"
                />
              )}
            </span>
          </TabButton>
        )}
      </div>
      {tab === 'summary' ? (
        <PortalSummaryCard />
      ) : tab === 'billing' ? (
        <PortalBilling ui={billing} paymentUrl={summary.agree_payment_url} />
      ) : (
        <PortalOnboarding />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'border-b-2 border-brand-royal px-3 py-2 -mb-px text-sm font-medium text-brand-navy'
          : 'border-b-2 border-transparent px-3 py-2 -mb-px text-sm text-brand-slate transition-colors hover:text-brand-navy'
      }
    >
      {children}
    </button>
  );
}

/**
 * Pick SignIn vs SignUp from the URL. Clerk's invitation-acceptance
 * link looks like:
 *
 *   /portal?__clerk_status=sign_up&__clerk_ticket=<jwt>
 *
 * The `sign_up` status means "this is a new user invited to an org —
 * render <SignUp> so they can set their password and the ticket can be
 * redeemed into a user account + org membership". Rendering <SignIn>
 * for that URL fails with "User not found" because the user doesn't
 * exist yet. Anything other than `sign_up` (returning customers, etc.)
 * falls through to <SignIn>.
 */
function isInvitationSignUp(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('__clerk_status') === 'sign_up';
}

export function PortalPage() {
  const showSignUp = isInvitationSignUp();
  return (
    <div className="grid gap-6">
      <Show when="signed-out">
        <div className="mx-auto max-w-md">
          {showSignUp ? (
            <SignUp
              routing="hash"
              signInUrl="/portal"
              forceRedirectUrl="/portal"
              signInForceRedirectUrl="/portal"
            />
          ) : (
            <SignIn
              routing="hash"
              signUpUrl="/portal"
              forceRedirectUrl="/portal"
              signUpForceRedirectUrl="/portal"
            />
          )}
        </div>
      </Show>
      <Show when="signed-in">
        <SignedInPortal />
      </Show>
    </div>
  );
}
